<script setup lang="ts">
import { computed } from 'vue';
import { ProgressState } from '../helpers/progress';
import { useFileSize } from '../composables/useFileSize';

const props = withDefaults(
    defineProps<{
        filename?: string | null;
        noFilenameMessage?: string;
        hideFileSize?: boolean;
        fileSize?: number | null;
        /**
         * Amount of bytes already uploaded
         */
        loadedSize?: number | null;
        loadedProgress?: number | null;
        progressState?: ProgressState;
        errorMessage?: string | null;
    }>(),
    {
        filename: null,
        noFilenameMessage: '-- geen bestandsnaam --',
        fileSize: null,
        loadedSize: null,
        loadedProgress: null,
        progressState: ProgressState.Ready,
        errorMessage: null,
    }
);

const showFileSize = computed(
    () => !props.hideFileSize && typeof props.fileSize === 'number'
);

const loadedSizeLabel = useFileSize(() => props.loadedSize);
const fileSizeLabel = useFileSize(() => props.fileSize);

const progressValue = computed<number | null>(() => {
    let progress: number;

    if (props.loadedProgress !== null) {
        progress = props.loadedProgress;
    } else if (
        props.fileSize !== null &&
        props.fileSize > 0 &&
        props.loadedSize !== null
    ) {
        progress = props.loadedSize / props.fileSize;
    } else {
        return null;
    }

    return Math.max(0, Math.min(progress, 1));
});

const progressPercentage = computed<string | null>(() => {
    if (progressValue.value === null) return null;
    return `${Math.round(progressValue.value * 100)}%`;
});

const progressStyle = computed<Record<string, string>>(() => {
    switch (props.progressState) {
        case ProgressState.Loading:
            return {
                width:
                    progressValue.value !== null
                        ? `${progressValue.value * 100}%`
                        : '50%',
            };
        case ProgressState.Waiting:
        case ProgressState.Finalizing:
            return {
                width: `100%`,
            };
        default:
            return {} as Record<string, string>;
    }
});

const progressLabel = computed<string | null>(() => {
    if (loadedSizeLabel.value !== null && fileSizeLabel.value !== null) {
        return `${loadedSizeLabel.value} / ${fileSizeLabel.value}`;
    } else if (fileSizeLabel.value !== null) {
        return `${fileSizeLabel.value}`;
    } else {
        return `${progressPercentage.value}`;
    }
});

const showErrorMessage = computed<boolean>(() => {
    return (
        props.progressState === ProgressState.Error &&
        props.errorMessage !== null
    );
});

const fileIcon = computed<string>(() => {
    switch (props.progressState) {
        case ProgressState.Done:
            return 'pi pi-check p-valid';
        case ProgressState.Error:
            return 'pi pi-exclamation-triangle p-error';
        default:
            return 'pi pi-file';
    }
});
</script>

<template>
    <div class="upload-file-progress-item" :class="[`state-${progressState}`]">
        <div class="progressbar" role="progressbar">
            <div class="progressbar-progress" :style="progressStyle" />
        </div>
        <div class="file-description">
            <i class="file-icon" :class="fileIcon" />
            <span
                class="filename mx-1"
                :class="{ 'no-filename': filename === null }"
            >
                <slot name="filename" :filename="filename">
                    {{ filename ?? noFilenameMessage }}
                </slot>
            </span>
            <span
                v-if="showErrorMessage"
                class="error-message p-error ml-2 font-italic"
            >
                {{ errorMessage }}
            </span>
            <span v-if="showFileSize" class="filesize text-400">{{
                progressLabel
            }}</span>
        </div>
    </div>
</template>

<style scoped lang="scss">
@keyframes progressbar-waiting-animation {
    0% {
        left: -35%;
        right: 100%;
    }
    60% {
        left: 100%;
        right: -60%;
    }
    100% {
        left: 100%;
        right: -60%;
    }
}

@keyframes progressbar-finalizing-animation-before {
    0% {
        left: 0;
        right: 100%;
    }
    100% {
        left: 0;
        right: 1rem;
    }
}
@keyframes progressbar-finalizing-animation-after {
    0% {
        left: 1rem;
        right: 0;
    }
    100% {
        left: 100%;
        right: 0;
    }
}

.upload-file-progress-item {
    position: relative;

    .progressbar {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 0;
        // background-color: var(--primary-200);
        border-radius: var(--border-radius);
        overflow: hidden;

        .progressbar-progress {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
        }
    }

    &.state-loading {
        .progressbar {
            .progressbar-progress {
                background-color: var(--surface-c);
            }
        }
    }

    &.state-waiting {
        .progressbar {
            .progressbar-progress {
                background-color: none;
                &::before {
                    content: '';
                    position: absolute;
                    background-color: var(--surface-c);
                    top: 0;
                    left: 0;
                    bottom: 0;
                    will-change: left, right;
                    animation: progressbar-waiting-animation 2.1s
                        cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
                }

                &::after {
                    content: '';
                    position: absolute;
                    background-color: var(--surface-c);
                    top: 0;
                    left: 0;
                    bottom: 0;
                    will-change: left, right;
                    animation: progressbar-waiting-animation 2.1s
                        cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
                    animation-delay: 1.15s;
                }
            }
        }
    }

    &.state-finalizing {
        .progressbar {
            .progressbar-progress {
                background-color: none;
                &::before {
                    content: '';
                    position: absolute;
                    background-color: var(--surface-c);
                    top: 0;
                    left: 0;
                    bottom: 0;
                    will-change: left, right;
                    animation: progressbar-finalizing-animation-before 2.1s
                        cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
                }

                &::after {
                    content: '';
                    position: absolute;
                    background-color: var(--surface-c);
                    top: 0;
                    left: 0;
                    bottom: 0;
                    will-change: left, right;
                    animation: progressbar-finalizing-animation-after 2.1s
                        cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
                }
            }
        }
    }

    &.state-done {
        .progressbar {
            .progressbar-progress {
                background-color: var(--surface-c);
                width: 100%;
            }
        }
    }

    .file-description {
        position: relative;
        padding: 0.5rem;
        z-index: 1;
        display: flex;
        align-items: center;

        .filename {
            white-space: nowrap;
            font-weight: 500;
            &.no-filename {
                color: var(--text-color-secondary);
                font-weight: lighter;
            }
        }

        .filesize {
            margin-left: auto;
            font-size: smaller;
        }
    }
}
</style>
